import { bookActions, loadingActions } from '@/actions';
import LegacyServiceCard from '@/components/elements/Card/ServiceListCard/LegacyServiceCard';
import ServiceListCard from '@/components/elements/Card/ServiceListCard/ServiceListCard';
import { Button, Fab } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import WaitlistModal from '@/components/modules/modals/WaitlistModal';
import { bookConstants } from '@/constants';
import { SMART_LINKS } from '@/constants/smartLinkConstants';
import {
  capitalizeFirstLetter,
  getBookingStartingPoint,
  getPlaceTimezone,
  getServiceCampaignIfAny,
  getServiceDuration,
  hasDynamicPricing,
  hoursFromSeconds,
  isSistaminuten,
  isToday,
  isTomorrow,
  makeReadableDay,
  scrollAccordionContentInView,
} from '@/helpers';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { getUnformattedServicePrice } from '@/helpers/price';
import { useAppDispatch, useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { useListCardsV2 } from '@/hooks/useListCardsV2';
import { __ } from '@/locale';
import { bookServices } from '@/services';
import { Place, PlaceEmployee, PlaceService, TimeSlot } from '@/types/state/shared';
import moment from 'moment';
import { useReducer } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export type ListViewItemReducerState = {
  isExpanded?: boolean;
  readMore?: {
    title: string;
    text: string;
  };
  showSubscribePopup?: number;
};

export type ListViewItemReducerAction =
  | {
      type: 'SET_READ_MORE';
      payload: {
        title: string;
        text: string;
      };
    }
  | {
      type: 'TOGGLE_EXPAND';
    }
  | {
      type: 'SHOW_SUBSCRIBE_POPUP';
      payload: number;
    }
  | {
      type: 'CLOSE_SUBSCRIBE_POPUP';
    };

const ListViewReducer = (state: ListViewItemReducerState, action: ListViewItemReducerAction) => {
  switch (action.type) {
    case 'SET_READ_MORE':
      return {
        ...state,
        readMore: {
          title: action.payload.title,
          text: action.payload.text,
        },
      };
    case 'TOGGLE_EXPAND':
      return {
        ...state,
        isExpanded: !state.isExpanded,
      };
    case 'SHOW_SUBSCRIBE_POPUP':
      return {
        ...state,
        showSubscribePopup: action.payload,
      };
    case 'CLOSE_SUBSCRIBE_POPUP':
      return {
        ...state,
        showSubscribePopup: undefined,
      };
    default:
      return state;
  }
};

const buildDay = (timestamp: number) => {
  if (isToday(timestamp)) {
    return __('today');
  }

  if (isTomorrow(timestamp)) {
    return __('tomorrow');
  }
  return makeReadableDay(timestamp);
};

const waitlistEnabled = (place: Place) => {
  return !!place.about.settings?.supportsWaitlist;
};

const ListViewItem = ({
  className,
  place,
  schedule,
  isOpen,
}: {
  className?: string;
  place: Place;
  schedule: {
    day: number;
    program: TimeSlot[];
  };
  isOpen: boolean;
}) => {
  const { isMobileView } = useMobileView();
  const isListCardsV2 = useListCardsV2();
  const ServiceCard = isListCardsV2 ? ServiceListCard : LegacyServiceCard;
  moment.tz.setDefault(getPlaceTimezone(place));
  const appDispatch = useAppDispatch();
  const user = useAppSelector((state) => state.users)?.user;
  const history = useHistory();
  const location = useLocation();
  const initialState: ListViewItemReducerState = {
    isExpanded: isOpen,
    readMore: {
      title: '',
      text: '',
    },
  };
  const [state, dispatch] = useReducer(ListViewReducer, initialState);

  const handleCollapse = (e) => {
    dispatch({ type: 'TOGGLE_EXPAND' });
    if (!state.isExpanded) {
      scrollAccordionContentInView(e.target);
    }
  };

  const book = (capacity, data, day) => async () => {
    let dynamicPriceListIdKey = bookConstants.DYNAMIC_PRICE_KEY_DEFAULT;
    if (hasDynamicPricing(data.service)) {
      appDispatch(loadingActions.show());
      try {
        const hasDynamicPrice = await bookServices.hasDynamicPrice(place.id, data.start);
        if (hasDynamicPrice?.dynamicPriceListIdKey) {
          dynamicPriceListIdKey = hasDynamicPrice.dynamicPriceListIdKey;
        }
      } catch (e) {}
      appDispatch(loadingActions.hide());
    }

    // @ts-ignore
    appDispatch(bookActions.bookAgain(place, [data.service], data.employee, parseInt(day, 10)));
    appDispatch(
      bookActions.pickHour(
        parseInt(day, 10),
        parseInt(data.from, 10),
        [data.employee.id],
        dynamicPriceListIdKey,
        data.resources,
        capacity,
        1,
      ),
    );

    bookingFlowTrackingSession.initiate(place, user, getBookingStartingPoint(), {
      service_ids: [data.service.id],
      employee_selection: 'any',
    });

    history.push({
      pathname: '/booking/checkout',
      state: { fromUrl: location.pathname },
    });
  };

  const showSubscribePopup = (index: number) => {
    dispatch({ type: 'SHOW_SUBSCRIBE_POPUP', payload: index });
  };

  const closeSubscribe = () => {
    dispatch({ type: 'CLOSE_SUBSCRIBE_POPUP' });
  };

  const handleShowReadMore = (title: string, text: string) => {
    dispatch({
      type: 'SET_READ_MORE',
      payload: {
        title,
        text,
      },
    });
  };

  const handleCloseReadMore = () => {
    dispatch({
      type: 'SET_READ_MORE',
      payload: {
        title: '',
        text: '',
      },
    });
  };

  const services = [];
  const employees = [];

  const collapseClass = state.isExpanded ? 'block' : 'hidden';

  const hideEmployees = Boolean(place.about?.settings?.hideEmployees);

  place.services.forEach((groups) => {
    groups.services.forEach((service) => {
      services[service.id] = service;
      services[service.id].groupOrder = groups.order || 0;
    });
  });

  place.employees.forEach((employee) => {
    employees[employee.id] = employee;
  });

  if (schedule && schedule.day) {
    const dayLabel = buildDay(schedule.day);

    const list: {
      capacity?: number;
      from?: number;
      fullbooked?: boolean;
      start?: string;
      service?: PlaceService & { groupOrder?: number };
      employee?: PlaceEmployee;
    }[] = [];

    schedule.program.forEach((day) => {
      const service = services[day.service] || {};
      const employee = employees[day.employee] || {};
      if (services[day.service] && employees[day.employee]) {
        list.push({
          from: day.from,
          service,
          employee,
          capacity: day.capacity,
          fullbooked: Boolean(day.capacity === 0),
          start: day.start,
        });
      }
    });

    list.sort((a, b) => {
      try {
        if (a.from === b.from) {
          // sort by service order
          if (a.service.groupOrder === b.service.groupOrder) {
            return a.service.sorting < b.service.sorting ? -1 : 1;
          }

          // sort by group order
          return a.service.groupOrder < b.service.groupOrder ? -1 : 1;
        }

        // Sort by from
        return a.from < b.from ? -1 : 1;
      } catch (e) {
        // Sort by from
        return a.from < b.from ? -1 : 1;
      }
    });

    const htmlList = list.map((item, k) => {
      const name = (item.service && item.service.name) || '';
      const serviceName = isMobileView
        ? capitalizeFirstLetter(name.split('.').join('. '))
        : capitalizeFirstLetter(name);

      const priceListId = item.employee && item.employee.about ? item.employee.about.priceListId : null;

      const { campaignService } = getServiceCampaignIfAny(item.service, place.campaigns, [item.service.id]);

      const unformattedServicePrice = getUnformattedServicePrice(item.service, priceListId, place);
      const discount: number[] = [];
      if (campaignService) {
        const offerPriceV2 = getUnformattedServicePrice(item.service, priceListId, place, campaignService);
        if (offerPriceV2?.value?.[0] && offerPriceV2.value[0] !== unformattedServicePrice.value[0]) {
          discount.push(unformattedServicePrice.value[0] - offerPriceV2.value[0]);
        }
        if (
          offerPriceV2?.value?.[1] &&
          offerPriceV2?.type === 'range' &&
          offerPriceV2?.value?.[1] !== unformattedServicePrice?.value?.[1]
        ) {
          discount.push(unformattedServicePrice.value[1] - offerPriceV2.value[1]);
        }
      }

      const serviceDuration = getServiceDuration(
        item.service,
        item.employee && item.employee.about ? item.employee.about.priceListId : null,
      );

      const smDiscount = isSistaminuten() && place?.about?.sistaminutenDiscount;
      const showWaitlist = item.fullbooked && waitlistEnabled(place);
      const showBookButton = !item.fullbooked;

      return (
        <ServiceCard
          key={k}
          id={item.service.id}
          employeeName={!hideEmployees && item.employee?.about?.name}
          price={unformattedServicePrice}
          name={serviceName}
          onShowReadMore={handleShowReadMore}
          duration={serviceDuration}
          onRowClick={() => {
            if (showBookButton) {
              book(item.capacity, item, schedule.day)();
            } else {
              showSubscribePopup(k);
            }
          }}
          timeslot={{ from: hoursFromSeconds(item.from), fullyBooked: item.fullbooked, capacity: item.capacity }}
          tags={{
            campaign: campaignService,
            waitingList: showWaitlist,
          }}
          discount={{ amounts: discount, percentage: smDiscount }}
          description={item?.service?.about?.description}
          rightSlot={
            (showBookButton || showWaitlist) && (
              <Button
                label={showBookButton ? __('book') : __('bookWaitingList')}
                onClick={(e) => {
                  e.stopPropagation();

                  if (showBookButton) {
                    book(item.capacity, item, schedule.day)();
                  } else {
                    showSubscribePopup(k);
                  }
                }}
                size="sm"
                variant={showBookButton ? 'primary' : 'secondary'}
              />
            )
          }
        />
      );
    });

    if (!list.length) {
      return null;
    }

    return (
      <>
        <div className={`item ${state.isExpanded && !isMobileView ? 'mb-md' : ''}`}>
          <div className="p-md border-border_regular flex items-center justify-between border-t">
            <h2 className="text-h-s">{capitalizeFirstLetter(dayLabel)}</h2>
            <Fab
              size="sm"
              variant="secondary"
              onClick={handleCollapse}
              rightIcon={<Icon variant={state.isExpanded ? 'chevron-up' : 'chevron-down'} color="fg_primary" />}
            />
          </div>

          <div
            id={className ? className + schedule.day : 'services' + schedule.day}
            className={'gap-sm accordion-content flex flex-col ' + collapseClass}
            role="tabpanel">
            {htmlList}
          </div>
        </div>
        {state.readMore.text && (
          <Modal className={`${isMobileView ? 'h-screen' : 'md:max-w-[560px]'}`} isOpen={Boolean(state.readMore?.text)}>
            <Modal.Content floating={!isMobileView} newDesign={true}>
              <div className={`relative flex flex-col ${isMobileView ? 'h-full w-full' : ''}`}>
                <Modal.Header
                  newDesign={true}
                  title={state.readMore?.title}
                  floating={!isMobileView}
                  onClose={handleCloseReadMore}
                />
                <div className={`p-xl gap-lg bg-surface_l4 flex h-full flex-col ${isMobileView ? '' : 'rounded-b-lg'}`}>
                  <span className="whitespace-pre-wrap">{state.readMore?.text}</span>
                </div>
              </div>
            </Modal.Content>
          </Modal>
        )}
        {state.showSubscribePopup >= 0 && (
          <WaitlistModal
            isOpen
            onRequestClose={closeSubscribe}
            deepLink={SMART_LINKS.getShareSmartLink(
              place.id,
              [list[state.showSubscribePopup]?.service?.id],
              hideEmployees ? undefined : list[state.showSubscribePopup]?.employee?.id,
            )}
          />
        )}
      </>
    );
  }

  return null;
};

export default ListViewItem;
